import {
    useState,
    useCallback,
} from 'react'
import compact from 'lodash/compact'
import {
    useDropzone,
} from 'react-dropzone'

import useUploadFile from './hook/useUploadFile'
import DropZone from './DropZone'

const DEFAULT_FILE_CONTEXT_TYPE = 'application/octet-stream'
const fileToOctetStreamBlob = async (file: File) => {
    return new Blob([new Uint8Array(await file.arrayBuffer())], {
        type: DEFAULT_FILE_CONTEXT_TYPE,
    })
}

type Props = {
    onChange: (value: string[]) => void,
    required?: boolean,
    disabled?: boolean,
    multiple?: boolean,
    acceptType?: string,
}

const defaultProps: Partial<Props> = {
    required: false,
    disabled: false,
    multiple: true,
    acceptType: undefined,
}

function DropZoneContainer({
    onChange,
    required,
    disabled,
    multiple,
    acceptType,
}: Readonly<Props>) {
    const [
        state,
        setState,
    ] = useState({
        uploaded: false,
        loaded: false,
    })

    const uploadFile = useUploadFile()

    const uploadFiles = useCallback((files: File[]) => {
        Promise.all(files.map(async (file) => {
            const formData = new FormData()

            formData.append('file', await fileToOctetStreamBlob(file), file.name)
            formData.append('name', file.name)
            return uploadFile(formData)
        })).then((attachmentsIds) => {
            onChange(compact(attachmentsIds))
        })
        setState({
            ...state,
            uploaded: true,
        })
    }, [
        state,
        uploadFile,
        onChange,
    ])

    const onDrop = useCallback((files) => {
        setState({
            ...state,
            loaded: true,
        })
        uploadFiles(files)
    }, [
        state,
        uploadFiles,
    ])

    const accept = acceptType ? {
        [`${acceptType}`]: [],
    } : {}

    const {
        getRootProps,
        getInputProps,
        open,
        isDragActive,
        isDragReject,
    } = useDropzone({
        onDrop,
        noClick: true,
        disabled,
        maxFiles: multiple ? 0 : 1,
        accept,
        multiple,
    })

    return (
        <DropZone
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            isDragReject={isDragReject}
            open={open}
            required={required}
            disabled={disabled}
            multiple={multiple}
        />
    )
}

DropZoneContainer.defaultProps = defaultProps

export default DropZoneContainer
